import type {BookingInterface} from '@/types/booking';
import type {TemplateInterface} from '@/store/modules/template';
import type {VehicleImageInterface, VehicleInterface} from '@/types/vehicle';
import type {User} from '@/types/user';
import type {TripExtra} from '@/types/trip-extra';
import type {TaxCategory} from '@/types/taxes';
import type {Nullable} from '@/types/utils';

export interface DraftListingInterface {
    name: string,
    pricing: {
        amount: number,
        price_period: 'Day',
    },
    vehicle: {
        year: number | null,
        make: string | null,
        model: string | null,
        color: string | null,
        plate: string | null,
    },
}

export interface ListingInterface {
    checkout_url?: string;
    loaded?: boolean,
    active: boolean,
    approved: boolean,
    archived_at: Nullable<string>,
    deleted_at?: Nullable<string>,
    cover_photo: Nullable<string>,
    is_custodian: boolean,
    custodian?: User,
    init?: boolean,
    l_id: Nullable<string>,
    unit_id?: Nullable<string>,
    listing_image: string,
    min_age: number,
    name: string,
    checkout_mode: string,
    order: number,
    paused: boolean,
    city?: string,
    state?: string
    description?: string,
    tax_category?: TaxCategory | null,
    seasoned_driver: number | null,
    create_mode: 'manual' | 'turo_import' | 'getaround_import' | 'tesla_import' | 'bouncie_import',
    pricing?: ListingPricing,
    vehicle?: VehicleInterface,
    listing_images: VehicleImageInterface[],
    bookings: BookingInterface[],
    miles_included: number,
    longer_weekend_trip_preferred: boolean,
    miles_extra: number,
    minimum_request_duration: Nullable<string>,
    padding_after: number | null,
    padding_before: number | null,
    provider_listings: any[] | null,
    slug: string,
    sub_title: string,
    template?: TemplateInterface | null,
    timezone: string,
    title: string,
    extras: TripExtra[],
    use_esign_template: boolean,
    use_secondary_driver: boolean,
    commission_partners: any[] | null,
    distance_allowance_max: number,
    age_requirements?: object,
    available?: {
        available: boolean,
        booking: BookingInterface | null,
    },
}


export type ListingFilter = {
    year: {
        selected: boolean,
        years: number[],
        available_years: number[],
    },
    make: {
        selected: boolean,
        makes: string[],
        available_makes: string[],
    },
    model: {
        selected: boolean,
        models: string[],
        available_models: string[],
    },
}

export type ListingPricing = {
    adjusted_amount: number,
    amount: number,
    discountable: boolean,
    taxable: boolean,
    original_price: number,
    price_period: 'Day' | string,
    price_structure: Nullable<string>,
    price_structure_id: Nullable<string>,
    price_type: 'flat_fee' | string,
    sec_dep: number | null,
    security_deposit_credit: number | null,
    security_deposit_debit: number | null,
    total_per_day: number,
    monthlyPercentageDiscount: number | null,
    threeDayPercentageDiscount: number | null,
    weeklyPercentageDiscount: number | null,
}

export enum ListingLoadingSubject {
    Listing = 'listing',
    Listings = 'listings',
    AddListing = 'addListing',
    SaveListing = 'saveListing',
    DeleteListing = 'deleteListing',
    ArchiveListing = 'archiveListing',
    Pausing = 'pausing',
    DecodingVin = 'decoding_vin',
    UnavailablePeriod = 'unavailable_period',
    MatchingTeslaVehicle = 'matchingTeslaVehicle',
    MatchBouncieVehicle = 'matchBouncieVehicle',
    TripExtraAdd = 'tripExtraAdd',
    TripExtraEdit = 'tripExtraEdit',
    ImportListings = 'import_listings',
    ImportImages = 'importImages',
    ImportDelete = 'importDelete',
    GetAllExtras = 'getAllExtras',
    SyncCarShareData = 'syncCarShareData',
    DailyPrices = 'dailyPrices',
    UpdateDailyPrices = 'updateDailyPrices',
    UpdateOdomProvider = 'updateOdomProvider',
    Vehicle = 'vehicle',
    VehicleDetails = 'vehicleDetails',
    GetIncidents = 'getIncidents',
    SaveIncident = 'saveIncident',
    DeleteIncident = 'deleteIncident',
    UpdateIncident = 'updateIncident',
    ListingExtras = 'listing_extras',
    VehicleFeatures = 'vehicleFeatures',
}